import type { Trip } from '../shared/trips.js';
import type { TripStatus } from '../shared/trip-api.js';
import { TripApiError } from './trip-api.js';
import { operation, type Observe, type ObservedOperation } from '../shared/observability.js';
import { observe } from './telemetry.js';
import { errorOutcome } from './observed-request.js';

export type SaveState = { status: 'saved' } | { status: 'saving' } | { status: 'draft' } | { status: 'unsaved'; message: string; retryMs?: number };
export interface SaveTimers {
  set: (callback: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clear: (timer: ReturnType<typeof setTimeout>) => void;
}
const timers: SaveTimers = { set: (callback, ms) => setTimeout(callback, ms), clear: clearTimeout };

/** One writer per trip. Edit numbers are local acknowledgements, not API revisions. */
export class TripSaver {
  private edit = 0;
  private savedEdit = 0;
  private pending?: { edit: number; trip: Trip; observation: ObservedOperation };
  private inFlight?: ObservedOperation;
  private running = false;
  private disposed = false;
  private failed?: Error;
  private retryMs = 2000;
  private timer?: ReturnType<typeof setTimeout>;
  private waiters: Array<{ edit: number; resolve: (revision: number) => void; reject: (error: Error) => void }> = [];

  constructor(public revision: number, private readonly put: (revision: number, trip: Trip, observation?: ObservedOperation) => Promise<TripStatus>,
    private readonly callbacks: { state: (state: SaveState) => void; saved: (status: TripStatus, trip: Trip) => void; conflict: () => void },
    private readonly clock: SaveTimers = timers, private readonly observer: Observe = observe) {}

  get hasEdits(): boolean { return this.running || this.pending !== undefined; }
  get latestEdit(): number { return this.edit; }

  save(trip: Trip): number {
    if (this.disposed) throw new Error('The trip editor is closed.');
    this.failed = undefined;
    this.pending?.observation.finish('superseded');
    this.inFlight?.finish('superseded');
    this.pending = { edit: ++this.edit, trip, observation: operation(this.observer, 'trip_save', { tripId: trip.id }) };
    if (!this.timer) void this.flush();
    return this.edit;
  }

  waitFor(edit: number): Promise<number> {
    if (this.disposed || this.failed) return Promise.reject(this.failed ?? new Error('The trip editor is closed.'));
    if (edit <= this.savedEdit) return Promise.resolve(this.revision);
    return new Promise((resolve, reject) => this.waiters.push({ edit, resolve, reject }));
  }

  dispose(): void {
    this.disposed = true;
    if (this.timer) this.clock.clear(this.timer);
    this.pending?.observation.finish('cancelled');
    this.inFlight?.finish('cancelled');
    this.pending = undefined;
    this.reject(new Error('The trip editor is closed.'));
  }

  private reject(error: Error): void {
    for (const waiter of this.waiters.splice(0)) waiter.reject(error);
  }
  private supersedePending(): void { this.pending?.observation.finish('superseded'); }

  private async flush(): Promise<void> {
    if (this.running || this.disposed || !this.pending) return;
    this.running = true;
    const sent = this.pending;
    this.inFlight = sent.observation;
    this.pending = undefined;
    this.callbacks.state({ status: 'saving' });
    try {
      const status = await this.put(this.revision, sent.trip, sent.observation);
      sent.observation.finish('success', { revision: status.revision });
      if (this.disposed) return;
      this.revision = status.revision;
      this.savedEdit = sent.edit;
      this.retryMs = 2000;
      this.callbacks.saved(status, sent.trip);
      const ready = this.waiters.filter((waiter) => waiter.edit <= sent.edit);
      this.waiters = this.waiters.filter((waiter) => waiter.edit > sent.edit);
      for (const waiter of ready) waiter.resolve(this.revision);
      if (!this.pending) this.callbacks.state({ status: 'saved' });
    } catch (caught) {
      if (this.disposed) return;
      const error = caught instanceof Error ? caught : new Error(String(caught));
      if (error instanceof TripApiError && error.code === 'TRIP_CONFLICT') {
        sent.observation.finish('rejected', { code: error.code, reason: 'conflict' });
        this.supersedePending();
        this.pending = undefined;
        this.failed = error;
        this.reject(error);
        this.callbacks.state({ status: 'unsaved', message: 'This trip changed on another device. Reloading the latest version…' });
        this.callbacks.conflict();
      } else {
        this.pending ??= sent;
        if (this.pending !== sent) sent.observation.finish('superseded');
        const retry = !(error instanceof TripApiError && error.status >= 400 && error.status < 500);
        this.callbacks.state({ status: 'unsaved', message: error.message, ...(retry ? { retryMs: this.retryMs } : {}) });
        if (retry) {
          this.timer = this.clock.set(() => { this.timer = undefined; void this.flush(); }, this.retryMs);
          this.retryMs = Math.min(30_000, this.retryMs * 2);
        } else {
          sent.observation.finish(errorOutcome(error), { reason: 'http' });
          this.failed = error;
          this.reject(error);
        }
      }
    } finally {
      this.inFlight = undefined;
      this.running = false;
      if (!this.disposed && this.pending && !this.timer && !this.failed) void this.flush();
    }
  }
}
